import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";

import { errorSwal, warningSwal } from "../../../helper/sweetalert";
import PriceOptionsModal from "./PriceOptionsModal";
import ProductCardList from "./ProductCardList";
import ProductTableList from "./ProductTableList";
import ProductVariantModal from "./ProductVariantModal";
import useScanDetection from "use-scan-detection";
import { GET_SHOP } from "../../../apollo/shop/Query";

import { SCAN_PRODUCT } from "../../../apollo/product/query";
import { GET_PRODUCT_VARIANTS } from "../../../apollo/productVariant/query";
import { SHOP_SETTING_KEY, USER_KEY } from "../../../const";

function Product({
  _skip,
  _limit,
  isTableList,
  selectedPrice,
  products,
  cartDatas,
  activeCart,
  updateOrder,
  addOrder,
  setCartDatas,
  originProducts, 
  optionPrice,
  productLoading,
  productDatas,
  shopData
}) {
  const setting = useMemo(() => {
    return JSON.parse(localStorage.getItem(SHOP_SETTING_KEY));
  }, []);
  const commissionEnable = setting?.commissionEnable ?? false;
  const shopId = JSON.parse(localStorage.getItem(USER_KEY))?.data?.shop?.id;
  const [showPriceOptionModal, setShowPriceOptionModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedProductPrice, setSelectedProductPrice] = useState(0);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [variantParent, setVariantParent] = useState(null);
  const [variantProducts, setVariantProducts] = useState([]);

  const [getProductFromBarcode] = useLazyQuery(SCAN_PRODUCT, { fetchPolicy: "network-only" });
  const [loadProductVariants, { data: productVariantData, loading: loadingVariants }] =
    useLazyQuery(GET_PRODUCT_VARIANTS, { fetchPolicy: "cache-and-network" });


  useEffect(() => {
    setVariantProducts(productVariantData?.productVariants?.data || []);
  }, [productVariantData]);

  const resolveCommissionChannel = useCallback(() => {
    const activeCartData = (cartDatas || []).find(
      (cart) => cart?.label === activeCart,
    );
    const rawChannel = activeCartData?.data?.orderGroup?.commissionPlatforms;
    if (Array.isArray(rawChannel)) {
      const fromArray = rawChannel[0]?.platform || rawChannel[0];
      return String(fromArray || "POS").toUpperCase();
    }
    return String(rawChannel || "POS").toUpperCase();
  }, [cartDatas, activeCart]);

  const filteredVariantProducts = useMemo(() => {
    if (!commissionEnable) return variantProducts || [];
    const channel = resolveCommissionChannel();
    return (variantProducts || []).filter((variant) => {
      const platforms = variant?.commissionPlatforms || [];
      if (!Array.isArray(platforms) || platforms.length === 0) {
        return true;
      }
      return platforms.some(
        (platform) =>
          String(platform?.platform || platform || "").toUpperCase() === channel,
      );
    });
  }, [variantProducts, resolveCommissionChannel]);

  const onSelectProduct = useCallback(
    (product) => {
      if (cartDatas?.length <= 0) {
        errorSwal("ກະລຸນາເພີ່ມໃບບີນ");
        return;
      }

      if (_.isEmpty(product)) return;
      setSelectedProduct(product);

      if (product?.hasVariant) {
        setVariantParent(product);
        setShowVariantModal(true);
        loadProductVariants({
          variables: {
            where: {
              product: product?.id,
            },
          },
        });
        return;
      }

      const checkOrder = cartDatas?.flatMap((item) => item?.data?.orders) || [];
      const filteredOrders = checkOrder.filter(
        (order) => order.product === product.id
      );
      const sumTotal = filteredOrders.reduce(
        (total, order) => total + order.amount,
        0
      );

      // function out stock update 7/31/2024
      const isLessStocks = shopData?.shop?.isLessStocks;

      if (!isLessStocks || isLessStocks) {
        if (!isLessStocks) {
          if (sumTotal >= product?.amount) {
            errorSwal(`ສິນຄ້າ  ${product?.name} ໝົດສະຕ໋ອກ`);
            return;
          }
        }
      }

      const _check = checkedProductSelected(product);

      // split amount order setting
      const {splitAmountOrder} = setting;
      if (!_check || splitAmountOrder || shopData?.shop?.id === '678db8f1942eb6002c1f40cd') {
        addOrder({ product, productPrice: 0 });
      } else {
        updateOrder({
          product,
          productId: product?.id,
          isAdded: true,
          changeValue: 0,
        });
      }
    },
    [cartDatas, addOrder, updateOrder, shopData, loadProductVariants]
  );

  const handleScanResult = useCallback((result) => {
    if (!result) return;
    const { type, product, productVariant } = result;
    console.log("=== SCAN RESULT ===", { type, product, productVariant });

    if (type === "VARIANT" && productVariant) {
      const attributes = (productVariant?.attributeValues || [])
        .map((attr) => {
          const name = attr?.attribute?.name || "";
          const value = attr?.value || "";
          return name ? `${name}${value ? `: ${value}` : ""}` : value;
        })
        .filter(Boolean)
        .join(", ");

      const productName = `${productVariant?.product?.name ?? product?.name ?? "-"}`;
      const variantName = attributes ? `${productName} (${attributes})` : productName;

      const normalizedVariant = {
        id: productVariant?.id,
        name: variantName,
        amount: productVariant?.amount ?? 0,
        originAmount: productVariant?.originAmount,
        originPrice: productVariant?.originPrice,
        price: productVariant?.price,
        currency: productVariant?.currency,
        commissionPlatforms:
          productVariant?.commissionPlatforms ||
          product?.commissionPlatforms ||
          [],
        productVariant: productVariant?.id,
        stock: product?._id,
        priceOptions: [],
        brand: product?.brand,
        category: product?.category,
        unitId: product?.unitId,
        unit: product?.unit,
        size: product?.size ?? null,
        dimension: product?.dimension ?? null,
        survivalId: product?.survivalId ?? null,
        image: productVariant?.image || product?.image,
        hasVariant: false,
      };

      console.log("=== NORMALIZED VARIANT ===", normalizedVariant);
      onSelectProduct(normalizedVariant);
    } else if (type === "PRODUCT" && product) {
      const normalizedProduct = { ...product, id: product?._id };
      onSelectProduct(normalizedProduct);
    }
  }, [onSelectProduct]);

  const getScanProduct = useCallback(async (barCode) => {
    if (!barCode) return;
    const respon = await getProductFromBarcode({
      variables: {
        where: {
          barCode: barCode,
          shop: shopId,
          isDeleted: false
        },
      },
    });
    return handleScanResult(respon?.data?.stocksByBarcode);
  }, [getProductFromBarcode, shopId, handleScanResult]);

  const _getScanProduct = useCallback(async (barCode) => {
    if (!barCode) return;
    const respon = await getProductFromBarcode({
      variables: {
        where: {
          barCode: barCode,
          shop: shopId,
          isDeleted: false
        },
      },
    });
    return handleScanResult(respon?.data?.stocksByBarcode);
  }, [getProductFromBarcode, shopId, handleScanResult]);

  useScanDetection({
    onComplete: (barCode) => {
      const cleanBarCode = barCode.replace(/Shift/g, '');
      getScanProduct(cleanBarCode);
    },
    minLength: 0,
  });

  const onSelectVariant = useCallback(
    (variant) => {
      if (!variant) return;
      if (cartDatas?.length <= 0) {
        errorSwal("ກະລຸນາເພີ່ມໃບບີນ");
        return;
      }

      const isLessStocks = shopData?.shop?.isLessStocks;
      const variantAmount = variant?.amount ?? 0;

      const checkOrder = cartDatas?.flatMap((item) => item?.data?.orders) || [];
      const filteredOrders = checkOrder.filter(
        (order) => order.product === variant?.id
      );
      const sumTotal = filteredOrders.reduce(
        (total, order) => total + order.amount,
        0
      );

      if (!isLessStocks && sumTotal >= variantAmount) {
        errorSwal(`ສິນຄ້າ  ${variantParent?.name ?? ""} ໝົດສະຕ໋ອກ`);
        return;
      }

      const attributes = (variant?.attributeValues || [])
        .map((attr) => {
          const name = attr?.attribute?.name || "";
          const value = attr?.value || "";
          return name ? `${name}${value ? `: ${value}` : ""}` : value;
        })
        .filter(Boolean)
        .join(", ");

      const productName = `${variant?.product?.name ?? variantParent?.name ?? "-"}`;
      const variantName = attributes ? `${productName} (${attributes})` : productName;

      const normalizedVariant = {
        id: variant?.id,
        name: variantName,
        amount: variantAmount,
        originAmount: variant?.originAmount,
        originPrice: variant?.originPrice,
        price: variant?.price,
        currency: variant?.currency,
        commissionPlatforms:
          variant?.commissionPlatforms ||
          variantParent?.commissionPlatforms ||
          [],
        productVariant: variant?.id,
        stock: variantParent?.id,
        priceOptions: [],
        brand: { name: variant?.product?.brandName },
        category: { name: variant?.product?.category?.name },
        unitId: variantParent?.unitId,
        unit: variantParent?.unit,
        size: variantParent?.size ?? null,
        dimension: variantParent?.dimension ?? null,
        survivalId: variantParent?.survivalId ?? null,
      };

      const cartData = _.find(cartDatas, { label: activeCart });
      const hasAdded = cartData?.data?.orders?.some(
        (order) => order?.product === variant?.id
      );

      if (!hasAdded) {
        addOrder({ product: normalizedVariant, productPrice: variant?.price ?? 0 });
      } else {
        updateOrder({
          product: normalizedVariant,
          productId: variant?.id,
          isAdded: true,
          changeValue: 0,
        });
      }

      setShowVariantModal(false);
    },
    [cartDatas, addOrder, updateOrder, activeCart, shopData, variantParent]
  );

  const onSelectOptionPrice = (product) => {
    setSelectedProduct(product);
    if (cartDatas?.length <= 0) return warningSwal("ກະລຸນາເພີ່ມໃບບີນ");

    let _check = checkedProductSelected(product);

    if (!_check && product?.priceOptions) {
      setShowPriceOptionModal(true);
      return;
    }
    return setShowPriceOptionModal(true);
  };

  const checkedProductSelected = (product) => {
    let _check = false;
    const cartData = _.find(cartDatas, { label: activeCart });
    cartData?.data?.orders?.forEach((productId) => {
      if (product?.id === productId?.product) _check = true;
    });
    return _check;
  };

  return (
    <div>
      {!isTableList ? (
        <ProductCardList
          products={products}
          onSelectProduct={onSelectProduct}
          checkedProductSelected={checkedProductSelected}
          selectedPrice={selectedPrice}
          optionPrice={optionPrice}
          productLoading={productLoading}
          productDatas={productDatas}
        />
      ) : (
        <ProductTableList
          _skip={_skip}
          _limit={_limit}
          productLoading={productLoading}
          productDatas={productDatas}
          products={products}
          onSelectProduct={onSelectProduct}
          setShowPriceOptionModal={setShowPriceOptionModal}
          checkedProductSelected={checkedProductSelected}
          onSelectOptionPrice={onSelectOptionPrice}
          selectedPrice={selectedPrice}
          optionPrice={optionPrice}
        />
      )}
      <PriceOptionsModal
        addOrder={addOrder}
        selectedProductPrice={selectedProductPrice}
        setSelectedProductPrice={setSelectedProductPrice}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        showPriceOptionModal={showPriceOptionModal}
        cartDatas={cartDatas}
        setCartDatas={setCartDatas}
        setShowPriceOptionModal={setShowPriceOptionModal}
        activeCart={activeCart}
      />
      <ProductVariantModal
        show={showVariantModal}
        onHide={() => setShowVariantModal(false)}
        variants={filteredVariantProducts}
        loading={loadingVariants}
        onSelectVariant={onSelectVariant}
        parentProduct={variantParent}
      />
    </div>
  );
}

export default Product;
